import { parseTimeOfDayMinutes } from "./time-slot";

/**
 * 注文書取込で片側だけ抽出できた時刻を補完するときの許容幅 (分)。
 * 運用値は `system_settings` が持ち、取込リクエストごとに渡す。
 * **0 は「補完しない」**を意味する (幅ゼロの枠は作らない。下記 `backfillImportTimeWindow` 参照)。
 */
export interface ImportTimeTolerance {
  /** 終了時刻だけ抽出できたとき、その何分前を開始にするか。 */
  earlyArrivalMinutes: number;
  /** 開始時刻だけ抽出できたとき、その何分後を終了にするか。 */
  lateArrivalMinutes: number;
}

/** 補完結果。`backfilled` は許容幅で埋めた側 (埋めなかった場合は null)。 */
export interface BackfilledTimeWindow {
  start: string | null;
  end: string | null;
  backfilled: "start" | "end" | null;
}

/** 同日内に収める下限 (00:00) と上限 (23:59)。0 時またぎは扱わない (#651)。 */
const DAY_START_MINUTES = 0;
const DAY_END_MINUTES = 23 * 60 + 59;

/**
 * 基準時刻から `deltaMinutes` ずらした "HH:MM" を返す。同日内 (00:00〜23:59) に丸める。
 * 早着側は負の delta を渡すため符号は問わない。数値でない場合だけ null。
 */
function shiftWithinDay(baseMinutes: number, deltaMinutes: number): string | null {
  if (!Number.isFinite(deltaMinutes)) return null;
  const clamped = Math.min(
    DAY_END_MINUTES,
    Math.max(DAY_START_MINUTES, baseMinutes + deltaMinutes),
  );
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * 補完に使える許容幅かどうか。**0 と負と非数値は補完しない**。
 *
 * 0 は「補完しない」の意味 (幅ゼロの枠は作らない)。負を通すと開始が終了より後になる逆転した枠を
 * 作り、確定時に `refineSameDayWindow` が「0時をまたぐ時間帯は設定できません」で弾くため、
 * 取込した注文が一切保存できなくなる。contract は min(0) で弾くが、`system_settings` を直接
 * 更新する運用 (max_ride_minutes と同じ経路) では負値が入りうる。
 */
function isUsableTolerance(minutes: number): boolean {
  return Number.isFinite(minutes) && minutes > 0;
}

/** 空文字・空白のみを未抽出 (null) とみなし、値は前後の空白を落として返す。 */
function normalize(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * 注文書から**片側の時刻しか抽出できなかった**組を、許容幅で埋めて時間幅にする (#657)。
 *
 * 注文書に「12:00」とだけ書かれた指定は、そのままだと片側が空のまま保存され、最適化側で
 * 便区分窓の端まで広げられてしまう (午前便なら 07:00〜12:00)。枠が数時間になるため、
 * 同時刻指定の注文でも抱き合わせが成立し、指定時刻から大きく外れた経路が組まれる。
 *
 * 補完は **空いている側だけ**に行い、**抽出できた時刻は書き換えない**。注文書の「12:00」が
 * 納品期限として書かれているのか開始時刻として書かれているのかは取込元が判断しており、
 * その意味を補完側で歪めないため (Cqree は期限として終了時刻へ写像する。#323)。
 *
 * - 開始=空 / 終了=12:00 → `11:45` 〜 `12:00` (早着許容 15 分の場合)
 * - 開始=12:00 / 終了=空 → `12:00` 〜 `12:15` (遅着許容 15 分の場合)
 * - 両方あり / 両方空 → そのまま (時刻の記載が無い組は補完しない)
 *
 * **幅ゼロになる補完は行わない。** 許容幅 0、または日の端で丸めた結果が元の時刻と同値になる
 * ケース (例: 納品期限 00:00 に早着許容を引くと 00:00 のまま) では片側指定のまま返す。
 * 開始と終了が同一時刻の枠は optimizer が 1 点の窓として解くため、移動時間補正込みの到着が
 * その時刻に一致しない注文がすべて未割当になり、便区分窓まで広がるより悪化する。
 *
 * これは**取込のサポート機能**であり、人が画面で明示的に時刻を空にした注文には適用しない
 * (枠の解決 `resolveWindowWithFit` は従来どおり便区分窓の端まで広げる)。
 */
/**
 * 補完を取り消して「補完前の状態」に戻す (#657)。値は `backfillImportTimeWindow` が
 * 正規化した trim 済みのものなので、生値と比較するより判定がぶれない。
 * 補完していない窓はそのまま返す。
 */
export function revertBackfill(window: BackfilledTimeWindow): BackfilledTimeWindow {
  if (window.backfilled === null) return window;
  return {
    start: window.backfilled === "start" ? null : window.start,
    end: window.backfilled === "end" ? null : window.end,
    backfilled: null,
  };
}

export function backfillImportTimeWindow(
  start: string | null | undefined,
  end: string | null | undefined,
  tolerance: ImportTimeTolerance,
): BackfilledTimeWindow {
  const rawStart = normalize(start);
  const rawEnd = normalize(end);
  const asIs = (s: string | null, e: string | null): BackfilledTimeWindow => ({
    start: s,
    end: e,
    backfilled: null,
  });
  if (rawStart !== null && rawEnd !== null) return asIs(rawStart, rawEnd);
  if (rawStart === null && rawEnd === null) return asIs(null, null);

  if (rawStart === null) {
    const endMinutes = parseTimeOfDayMinutes(rawEnd);
    // HH:MM として解釈できない値は補完の起点にできないので、そのまま返す。
    if (endMinutes === null || !isUsableTolerance(tolerance.earlyArrivalMinutes)) {
      return asIs(null, rawEnd);
    }
    const filled = shiftWithinDay(endMinutes, -tolerance.earlyArrivalMinutes);
    if (filled === null || filled === rawEnd) return asIs(null, rawEnd);
    return { start: filled, end: rawEnd, backfilled: "start" };
  }

  const startMinutes = parseTimeOfDayMinutes(rawStart);
  if (startMinutes === null || !isUsableTolerance(tolerance.lateArrivalMinutes)) {
    return asIs(rawStart, null);
  }
  const filled = shiftWithinDay(startMinutes, tolerance.lateArrivalMinutes);
  if (filled === null || filled === rawStart) return asIs(rawStart, null);
  return { start: rawStart, end: filled, backfilled: "end" };
}

/** 集荷開始が配達開始以降なら true (どちらか未設定なら検証対象外)。 */
function conflictsPickupAfterDelivery(
  pickupStart: string | null,
  deliveryStart: string | null,
): boolean {
  return pickupStart !== null && deliveryStart !== null && pickupStart >= deliveryStart;
}

/**
 * 補完が「集荷開始 >= 配達開始」を作る場合、衝突が解ける**最小限の補完だけ**取り消す (#657 / #754)。
 *
 * contract の `refinePickupBeforeDelivery` は集荷開始が配達開始以降だと 400 を返す。補完前は
 * 片側が null で同検証がスルーされていたため、補完をそのまま書くと**受注編集フォームから
 * 二度と保存できない受注**を作る。取込・単件 CRUD・一括補完のどこから補完してもこの規則を通す
 * (取込だけが守っていて一括補完が素通しだった。#754)。
 *
 * 取り消しは集荷側から試す。集荷の開始を補完したことが引き金なら、それを戻すだけで衝突は解け、
 * **本来の目的である配達側の枠を守れる**。集荷の開始が注文書由来 (取り消しても動かない) の場合だけ
 * 配達側も戻す。補完前から前後関係が崩れている受注は、補完の有無で結果が変わらないので触らない。
 */
export function resolvePickupDeliveryBackfill(
  delivery: BackfilledTimeWindow,
  pickup: BackfilledTimeWindow,
): { delivery: BackfilledTimeWindow; pickup: BackfilledTimeWindow; revertedReason: string | null } {
  const keep = { delivery, pickup, revertedReason: null };
  if (delivery.backfilled === null && pickup.backfilled === null) return keep;
  if (!conflictsPickupAfterDelivery(pickup.start, delivery.start)) return keep;
  // 補完を取り消した状態が「補完前」。値は正規化済みなので生値と比べるよりぶれない。
  const originalPickup = revertBackfill(pickup);
  const originalDelivery = revertBackfill(delivery);
  if (conflictsPickupAfterDelivery(originalPickup.start, originalDelivery.start)) return keep;

  if (!conflictsPickupAfterDelivery(originalPickup.start, delivery.start)) {
    return {
      delivery,
      pickup: originalPickup,
      revertedReason: "集荷時刻は配達時刻との前後関係が崩れるため補完しませんでした",
    };
  }
  return {
    delivery: originalDelivery,
    pickup: originalPickup,
    revertedReason: "集荷時刻と配達時刻の前後関係が崩れるため時刻を補完しませんでした",
  };
}
